import i18n from "i18next";
import I18nextBrowserLanguageDetector from "i18next-browser-languagedetector";
import Backend from "i18next-http-backend";
import { Suspense, createContext, useEffect, useState } from "react";
import { initReactI18next, useTranslation } from "react-i18next";
import CircularLoadingIndicator from "../components/CircularLoadingIndicator";
import {
  LOCAL_STORAGE_DATA,
  LocalStorageController,
} from "../utils/LocalStorageController";
import { isNotNullOrUndefined } from "../utils/isNotNullOrUndefined";
import React from "react";
import { TranslationKeys, SupportedLanguageCode } from "../types/types";

const TranslationProviderContext = createContext({
  getTranslationText: (key: keyof TranslationKeys, data?: {}): any => {},
  updateAppLanguageCode: async (
    newLanguageCode: SupportedLanguageCode
  ): Promise<void> => {},
  appLanguageCode: "" as SupportedLanguageCode,
});

export const TranslationProvider = ({ children }: { children: any }) => {
  const { t } = useTranslation();
  const [appLanguageCode, setAppLanguageCode] =
    useState<SupportedLanguageCode>("en");
  useEffect(() => {
    (async () => {
      const localLanguageCode = LocalStorageController.connect().getData(
        LOCAL_STORAGE_DATA.APP_LANGUAGE_CODE,
        "string"
      );

      if (isNotNullOrUndefined(localLanguageCode)) {
        setAppLanguageCode(localLanguageCode as SupportedLanguageCode);
        return;
      }
    })();
  }, []);

  useEffect(() => {
    (async function init() {
      await i18n
        .use(Backend)
        .use(I18nextBrowserLanguageDetector)
        .use(initReactI18next) // passes i18n down to react-i18next
        .init({
          lng: appLanguageCode,
          fallbackLng: "en",
          debug: true,
          interpolation: {
            escapeValue: false, // react already safes from xss
          },
        });
    })();
  }, [appLanguageCode]);

  const getTranslationText = (key: keyof TranslationKeys, data?: {}) => {
    return isNotNullOrUndefined(t) ? t!(key, data || {}) : "";
  };
  const updateAppLanguageCode = async (
    newLanguageCode: SupportedLanguageCode
  ) => {
    setAppLanguageCode(newLanguageCode);
    LocalStorageController.connect().storeData(
      LOCAL_STORAGE_DATA.APP_LANGUAGE_CODE,
      newLanguageCode
    );
  };

  return (
    <TranslationProviderContext.Provider
      value={{ getTranslationText, updateAppLanguageCode, appLanguageCode }}
    >
      <Suspense fallback={<CircularLoadingIndicator />}>{children}</Suspense>
    </TranslationProviderContext.Provider>
  );
};
export default TranslationProviderContext;
